import './styles.scss';

import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Table, Input, Button, StatusChip, MetricCard, Popper } from '@fragments';
import {
    IconSearch,
    IconClipboardCheck,
    IconUsers,
    IconArrowRight,
    IconCalendar,
    IconX,
    IconChevronDown,
} from '@tabler/icons-react';
import dayjs from 'dayjs';

export default function SubmissionsView({
    submissions,
    insights,
    loading,
    page,
    pages,
    records,
    search,
    formId,
    forms,
    handleSearch,
    handleSelectPage,
    handleViewSubmission,
    handleFormFilter,
}) {
    const { t } = useTranslation('settings');
    const [inputValue, setInputValue] = useState(search);
    const formPopperRef = useRef();
    const formTriggerRef = useRef();

    const selectedForm = forms.find(f => f.id === formId) ?? null;

    function commitSearch() {
        if (inputValue !== search) handleSearch(inputValue);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') commitSearch();
    }

    function openFormPopper(e) {
        e.stopPropagation();
        const options = forms.map(form => ({
            id: form.id,
            label: form.title,
            onClick: () => handleFormFilter(form.id),
        }));
        formPopperRef.current.open(options, 'bottom-start', formTriggerRef.current);
    }

    function clearFormFilter(e) {
        e.stopPropagation();
        handleFormFilter(null);
    }

    const columns = [
        {
            id: 'form_title',
            title: t('forms.submissions.columns.form'),
            grow: 1,
            customRender: (item) => (
                <div className="submission-form-cell">
                    <div className="submission-form-icon">
                        <IconClipboardCheck size={14} />
                    </div>
                    <Text.span contrast={8}>{item.form?.title || '—'}</Text.span>
                </div>
            ),
        },
        {
            id: 'username',
            title: t('forms.submissions.columns.user'),
            minWidth: 180,
            customRender: (item) => (
                <Text.span contrast={7}>{item.user?.username || '—'}</Text.span>
            ),
        },
        {
            id: 'email',
            title: t('forms.submissions.columns.email'),
            minWidth: 200,
            customRender: (item) => (
                <Text.span contrast={5}>{item.user?.email || '—'}</Text.span>
            ),
        },
        {
            id: 'completed_at',
            title: t('forms.submissions.columns.answeredAt'),
            minWidth: 160,
            customRender: (item) => {
                if (item.completed && item.completed_at) {
                    return (
                        <Text.span contrast={5}>
                            {dayjs(item.completed_at).format(t('forms.submissions.dateTimeFormat'))}
                        </Text.span>
                    );
                }
                return <StatusChip.Alert text={t('forms.submissions.inProgress')} />;
            },
        },
    ];

    const formsFilledValue = insights?.forms_filled ?? 0;
    const uniqueUsersValue = insights?.unique_users ?? 0;
    const growthWeek = insights?.growth_last_week ?? 0;
    const growthMonth = insights?.growth_last_month ?? 0;

    return (
        <div className="submissions-view px-4">

            <div className="submissions-insights">
                <div className="insights-cards-row">
                    <MetricCard
                        title={t('forms.submissions.metrics.formsFilled')}
                        leftIcon={<IconClipboardCheck size={15} />}
                        value={formsFilledValue}
                        variant="number"
                        growth={growthWeek}
                        growthLabel="na semana"
                    />
                    <MetricCard
                        title={t('forms.submissions.metrics.uniqueUsers')}
                        leftIcon={<IconUsers size={15} />}
                        value={uniqueUsersValue}
                        variant="number"
                    />
                    <MetricCard
                        title={t('forms.submissions.metrics.monthlyGrowth')}
                        leftIcon={<IconCalendar size={15} />}
                        value={`${growthMonth}%`}
                        growth={growthMonth}
                    />
                </div>

                <div className="insight-search">
                    <div
                        ref={formTriggerRef}
                        className={`form-filter-trigger${selectedForm ? ' has-value' : ''}`}
                        onClick={openFormPopper}
                    >
                        <IconClipboardCheck size={14} className="form-filter-icon" />
                        <span className="form-filter-label">
                            {selectedForm ? selectedForm.title : t('forms.submissions.formFilter')}
                        </span>
                        {selectedForm ? (
                            <button className="form-filter-clear" onClick={clearFormFilter}>
                                <IconX size={12} />
                            </button>
                        ) : (
                            <IconChevronDown size={14} className="form-filter-icon" />
                        )}
                    </div>

                    <div className="search-input-wrapper">
                        <Input.Text
                            value={inputValue}
                            onInput={setInputValue}
                            onBlur={commitSearch}
                            onKeyDown={handleKeyDown}
                            placeholder={t('forms.submissions.searchPlaceholder')}
                            start={<IconSearch size={16} />}
                            fullWidth
                        />
                    </div>

                    <Popper
                        controlRef={formPopperRef}
                        config={{ searchKeyExtractor: (opt, validate) => validate(opt.label) }}
                    />
                </div>
            </div>

            <div className="submissions-table-section">
                <div className="submissions-table-header">
                    <Text.h5 contrast={7}>
                        {loading
                            ? t('common:actions.loading')
                            : t('forms.submissions.count', { count: records })}
                    </Text.h5>
                </div>

                <Table
                    id="community-submissions-table"
                    columns={columns}
                    data={submissions}
                    loading={loading}
                    loadingRows={5}
                    enablePagination={pages > 1}
                    page={page}
                    pages={pages}
                    records={records}
                    contained
                    onSelectPage={handleSelectPage}
                    onRowClick={handleViewSubmission}
                    emptyMessage={t('forms.submissions.emptyMessage')}
                    emptySubtitle={t('forms.submissions.emptySubtitle')}
                    emptyIcon={<IconClipboardCheck size={36} />}
                    actionsComponent={(item) => (
                        <Button
                            size="small"
                            color="inverse"
                            endIcon={<IconArrowRight size={14} />}
                            onClick={() => handleViewSubmission(item)}
                        >
                            {t('forms.submissions.viewDetails')}
                        </Button>
                    )}
                />
            </div>
        </div>
    );
}
